import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format } from 'date-fns';
import { 
  Settings, 
  Trash2, 
  Edit, 
  Calendar, 
  Users, 
  DollarSign,
  AlertTriangle,
  Target
} from 'lucide-react';

interface SlotSummary {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  booked: number;
  price?: number;
}

interface BulkSlotManagementProps {
  isOpen: boolean;
  onClose: () => void;
  onBulkEdit: (slots: any[], updates: any) => Promise<void>;
  onBulkDelete: (slotIds: string[]) => Promise<void>;
  slots: SlotSummary[];
  isLoading?: boolean;
}

export function BulkSlotManagement({ 
  isOpen, 
  onClose, 
  onBulkEdit, 
  onBulkDelete, 
  slots, 
  isLoading = false 
}: BulkSlotManagementProps) {
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('edit');
  const [bulkUpdates, setBulkUpdates] = useState({
    capacity: '',
    price: '',
    status: 'available' as 'available' | 'fully-booked' | 'cancelled'
  });

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    // Convert indeterminate to boolean
    const isChecked = checked === true;
    
    if (isChecked) {
      const allSlotIds = slots.map(slot => slot.id);
      setSelectedSlots(allSlotIds);
      console.log('✅ Selected all slots:', allSlotIds.length);
    } else {
      setSelectedSlots([]);
      console.log('❌ Deselected all slots');
    }
  };

  const handleSelectSlot = (slotId: string, checked: boolean | 'indeterminate') => {
    // Convert indeterminate to boolean
    const isChecked = checked === true;
    
    if (isChecked) {
      setSelectedSlots(prev => {
        if (!prev.includes(slotId)) {
          const newSelection = [...prev, slotId];
          return newSelection;
        }
        return prev;
      });
    } else {
      setSelectedSlots(prev => {
        const newSelection = prev.filter(id => id !== slotId);
        return newSelection;
      });
    }
  };

  const handleBulkEdit = async () => {
    if (selectedSlots.length === 0) {
      console.warn('⚠️ No slots selected for bulk edit');
      return;
    }

    console.log('📝 Starting bulk edit:', { 
      selectedCount: selectedSlots.length, 
      updates: bulkUpdates 
    });

    const updates: any = {};
    if (bulkUpdates.capacity) updates.capacity = parseInt(bulkUpdates.capacity);
    if (bulkUpdates.price) updates.price = parseFloat(bulkUpdates.price);
    if (bulkUpdates.status) updates.status = bulkUpdates.status;

    // Validate that at least one update is provided
    if (Object.keys(updates).length === 0) {
      console.warn('⚠️ No updates provided for bulk edit');
      return;
    }

    // Find the full slot data for each selected slot ID
    const slotsToUpdate = selectedSlots.map(id => {
      const fullSlotData = slots.find(slot => slot.id === id);
      if (!fullSlotData) {
        console.warn('⚠️ Could not find full data for slot:', id);
        return { id, ...updates };
      }
      
      // Merge existing slot data with updates
      return {
        id: fullSlotData.id,
        startTime: updates.startTime || fullSlotData.startTime,
        endTime: updates.endTime || fullSlotData.endTime,
        capacity: updates.capacity || fullSlotData.capacity,
        price: updates.price || fullSlotData.price,
        status: updates.status || 'available',
        booked: fullSlotData.booked || 0,
        date: fullSlotData.date,
        title: fullSlotData.title
      };
    });

    try {
      console.log('🚀 Executing bulk edit with updates:', {
        selectedSlots: selectedSlots.length,
        sampleSlot: slotsToUpdate[0],
        updates
      });
      await onBulkEdit(slotsToUpdate, updates);
      
      // Reset form and close
      setSelectedSlots([]);
      setBulkUpdates({ capacity: '', price: '', status: 'available' });
      console.log('✅ Bulk edit completed successfully');
      onClose();
    } catch (error) {
      console.error('❌ Error in bulk edit:', error);
      // Don't close modal on error, let user retry
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSlots.length === 0) {
      console.warn('⚠️ No slots selected for bulk delete');
      return;
    }

    console.log('🗑️ Starting bulk delete:', { 
      selectedCount: selectedSlots.length,
      selectedSlots,
      hasBookedSlots,
      totalBookings
    });

    try {
      console.log('🚀 Executing bulk delete');
      await onBulkDelete(selectedSlots);
      
      // Reset selection and close
      setSelectedSlots([]);
      console.log('✅ Bulk delete completed successfully');
      onClose();
    } catch (error) {
      console.error('❌ Error in bulk delete:', error);
      // Don't close modal on error, let user retry
    }
  };

  const selectedSlotsData = slots.filter(slot => selectedSlots.includes(slot.id));
  const totalBookings = selectedSlotsData.reduce((sum, slot) => sum + slot.booked, 0);
  const hasBookedSlots = selectedSlotsData.some(slot => slot.booked > 0);

  // Enhanced Select All checkbox state calculation
  const selectAllState = (() => {
    if (selectedSlots.length === 0) return false;
    if (selectedSlots.length === slots.length) return true;
    return 'indeterminate';
  })();



  useEffect(() => {
    // Reset state when modal opens
    if (isOpen) {
      setSelectedSlots([]);
      setBulkUpdates({ capacity: '', price: '', status: 'available' });
      setActiveTab('edit');
      console.log('🔄 Modal opened - State reset');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl border border-slate-200/60 shadow-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-200/50 flex items-center justify-center shadow-sm">
              <Settings className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-slate-900">Bulk Slot Management</DialogTitle>
              <p className="text-sm text-slate-600 mt-1">Manage multiple activity slots efficiently</p>
            </div>
          </div>
        </DialogHeader>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border border-slate-200/60 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-slate-900">{slots.length}</div>
                  <div className="text-xs text-slate-600">Total Slots</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200/60 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-600" />
                <div>
                  <div className="text-2xl font-bold text-slate-900">{selectedSlots.length}</div>
                  <div className="text-xs text-slate-600">Selected</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200/60 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-600" />
                <div>
                  <div className="text-2xl font-bold text-slate-900">{totalBookings}</div>
                  <div className="text-xs text-slate-600">Bookings in Selected</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-50/80 p-1 rounded-lg shadow-sm border border-slate-200/60 h-12">
            <TabsTrigger 
              value="edit" 
              className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
            >
              <Edit className="w-4 h-4" />
              Bulk Edit
            </TabsTrigger>
            <TabsTrigger 
              value="delete" 
              className="flex items-center gap-2 data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
            >
              <Trash2 className="w-4 h-4" />
              Bulk Delete
            </TabsTrigger>
          </TabsList>

          {/* Slot Selection */}
          <Card className="border border-slate-200/60 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-700">Select Slots to Manage</CardTitle>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all"
                    checked={selectAllState}
                    onCheckedChange={handleSelectAll}
                  />
                  <Label htmlFor="select-all" className="text-sm text-slate-600">
                    Select All ({slots.length})
                  </Label>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="max-h-64 overflow-y-auto space-y-2">
                {slots.map((slot) => {
                  const bookingPercentage = (slot.booked / slot.capacity) * 100;
                  return (
                    <div key={slot.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200/60 hover:bg-slate-50 transition-colors">
                      <Checkbox
                        id={slot.id}
                        checked={selectedSlots.includes(slot.id)}
                        onCheckedChange={(checked) => handleSelectSlot(slot.id, checked as boolean | 'indeterminate')}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-900 truncate">{slot.title}</h4>
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              bookingPercentage >= 90 ? 'bg-red-100 text-red-800' :
                              bookingPercentage >= 70 ? 'bg-amber-100 text-amber-800' :
                              bookingPercentage >= 30 ? 'bg-blue-100 text-blue-800' :
                              'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {slot.booked}/{slot.capacity}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-600">
                          <span>{format(new Date(slot.date), 'MMM dd, yyyy')}</span>
                          <span>{slot.startTime} - {slot.endTime}</span>
                          {slot.price && <span>${slot.price}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <TabsContent value="edit" className="space-y-6">
            {selectedSlots.length === 0 ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Please select at least one slot to perform bulk edits.
                </AlertDescription>
              </Alert>
            ) : (
              <Card className="border border-slate-200/60 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Edit {selectedSlots.length} Selected Slots
                  </CardTitle>
                  <p className="text-sm text-slate-600">
                    Changes will be applied to all selected slots. Leave fields empty to keep current values.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bulk-capacity" className="text-sm font-medium text-slate-700">
                        New Capacity
                      </Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="bulk-capacity"
                          type="number"
                          min="1"
                          placeholder="Leave empty to keep current"
                          value={bulkUpdates.capacity}
                          onChange={(e) => setBulkUpdates(prev => ({ ...prev, capacity: e.target.value }))}
                          className="pl-10 border-slate-200/80 focus:border-primary focus:ring-primary/20"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bulk-price" className="text-sm font-medium text-slate-700">
                        New Price ($)
                      </Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="bulk-price"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Leave empty to keep current"
                          value={bulkUpdates.price}
                          onChange={(e) => setBulkUpdates(prev => ({ ...prev, price: e.target.value }))}
                          className="pl-10 border-slate-200/80 focus:border-primary focus:ring-primary/20"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bulk-status" className="text-sm font-medium text-slate-700">
                      Status
                    </Label>
                    <Select value={bulkUpdates.status} onValueChange={(value: any) => setBulkUpdates(prev => ({ ...prev, status: value }))}>
                      <SelectTrigger className="border-slate-200/80 focus:border-primary focus:ring-primary/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="fully-booked">Fully Booked</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3 pt-4">
                    <Button
                      onClick={handleBulkEdit}
                      className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading || selectedSlots.length === 0 || Object.keys(bulkUpdates).every(key => !bulkUpdates[key as keyof typeof bulkUpdates])}
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Edit className="w-4 h-4 mr-2" />
                          Update {selectedSlots.length} Slot{selectedSlots.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                    {selectedSlots.length > 0 && (
                      <div className="text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded-lg border">
                        {selectedSlots.length} slot{selectedSlots.length !== 1 ? 's' : ''} selected
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="delete" className="space-y-6">
            {selectedSlots.length === 0 ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Please select at least one slot to perform bulk deletion.
                </AlertDescription>
              </Alert>
            ) : (
              <Card className="border border-red-200/60 shadow-sm bg-red-50/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-red-900 flex items-center gap-2">
                    <Trash2 className="w-5 h-5" />
                    Delete {selectedSlots.length} Selected Slots
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasBookedSlots && (
                    <Alert className="border-amber-200 bg-amber-50">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        Warning: Some selected slots have existing bookings ({totalBookings} total bookings). 
                        Deleting these slots may affect customer reservations.
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="p-4 bg-white rounded-lg border border-red-200/60">
                    <h4 className="font-medium text-slate-900 mb-2">This action will permanently delete:</h4>
                    <ul className="space-y-1 text-sm text-slate-600">
                      <li>• {selectedSlots.length} activity slots</li>
                      <li>• {totalBookings} associated bookings</li>
                      <li>• All related scheduling data</li>
                    </ul>
                  </div>
                  <div className="flex items-center gap-3 pt-4">
                    <Button
                      variant="destructive"
                      onClick={handleBulkDelete}
                      className="bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading || selectedSlots.length === 0}
                    >
                      {isLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete {selectedSlots.length} Slot{selectedSlots.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                    {selectedSlots.length > 0 && (
                      <div className="text-xs text-slate-600 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
                        {selectedSlots.length} slot{selectedSlots.length !== 1 ? 's' : ''} selected • {totalBookings} booking{totalBookings !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-200/60">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-200/80 hover:bg-slate-50"
            disabled={isLoading}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
